<template>
    <div>
        <v-col cols="12" class="tabs">
            <div class="d-flex pb-4">
                <v-col cols="12" md="6" class="grid-title py-2 px-0 d-flex align-center">
                    <h2 class="mb-0">{{ t('quality-control::nequas.page.title.neqas_results_reports') }}</h2>
                </v-col>
                <v-col cols="12" md="6" class="d-flex justify-content-end py-2">
                    <div v-if="can('api.v1.neqas-reports.index')">
                        <div class="px-2">
                            <v-btn color="primary" rounded text small @click="$refs.previewModal.openReportsModal()">
                                {{ t('quality-control::nequas.button.view_reports') }}
                            </v-btn>
                        </div>
                    </div>
                    <div class="px-2">
                        <v-tooltip top transition="v-fab-transition">
                            <template #activator="{ on, attrs }">
                                <v-btn color="primary" dark icon v-bind="attrs" v-on="on" @click="showFilters = true">
                                    <v-icon>mdi-filter</v-icon>
                                </v-btn>
                            </template>
                            <span>{{ t('default::button.filters') }}</span>
                        </v-tooltip>
                    </div>
                </v-col>
            </div>

            <div v-if="showFilters" class="filters filters-form w-100 pb-4">
                <v-slide-y-transition>
                    <v-row v-show="showFilters" class="filters-fields m-0">
                        <div class="px-1 w-100">
                            <v-col cols="12" class="d-flex align-center justify-content-between pb-0">
                                <div class="d-flex align-center">
                                    <v-icon left>mdi-filter</v-icon>
                                    <h6 class="mb-0">{{ t('filters::title.filter') }}</h6>
                                </div>
                                <div>
                                    <v-tooltip top transition="v-fab-transition">
                                        <template #activator="{ on, attrs }">
                                            <v-btn
                                                v-if="can('api.v1.patients.store') && can('patients.create')"
                                                color="primary"
                                                icon
                                                v-bind="attrs"
                                                v-on="on"
                                                @click="showFilters = false"
                                            >
                                                <v-icon>mdi-close</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>{{ t('default::button.close') }}</span>
                                    </v-tooltip>
                                </div>
                            </v-col>
                            <v-col cols="12" class="pt-0 pb-6">
                                <v-row class="m-0">
                                    <v-row>
                                        <v-col cols="4">
                                            <b-filter-date
                                                v-model="initialFilters.filter_created_at"
                                                range
                                                :label="t('audit-logs::filters.label.date')"
                                                @clear="reset"
                                            ></b-filter-date>
                                        </v-col>
                                        <v-col cols="auto" class="filters-actions bottom">
                                            <v-btn text rounded small color="primary" class="mr-4" @click="reset">
                                                {{ t('filters::button.reset') }}
                                            </v-btn>
                                            <v-btn rounded small color="primary" @click="doSearch">
                                                {{ t('filters::button.filter') }}
                                            </v-btn>
                                        </v-col>
                                    </v-row>
                                </v-row>
                            </v-col>
                        </div>
                    </v-row>
                </v-slide-y-transition>
            </div>

            <div class="tabs-top-actions">
                <div class="top-actions">
                    <div v-if="can('api.v1.neqas-reports.store')" class="period">
                        <span v-if="period" v-text="period"></span>
                        <span v-else v-text="t('quality-control::nequas.hint.generate_reports')"></span>
                    </div>
                    <div class="action">
                        <v-btn
                            v-if="can('api.v1.neqas-reports.store')"
                            color="primary"
                            :disabled="!gridFiltered || loading"
                            :loading="loading"
                            rounded
                            small
                            @click="generateReport"
                            >{{ t('quality-control::nequas.button.generate_reports') }}
                        </v-btn>
                    </div>
                </div>
            </div>
            <v-tabs v-model="tab" show-arrows class="mt-1">
                <v-tab v-for="(item, index) in items" :key="index">
                    <div class="tab">
                        {{ item }}
                    </div>
                </v-tab>

                <v-tab-item v-for="(item, index) in items" :key="item + index">
                    <neqas-samples-data-table
                        :ref="'table' + index"
                        :list-route="listRoute"
                        :initial-filters="initialFilters"
                        :columns="columns"
                        :hidden-columns="hiddenColumns"
                    ></neqas-samples-data-table>
                </v-tab-item>
            </v-tabs>
        </v-col>
        <neqas-sample-reports-modal ref="previewModal"></neqas-sample-reports-modal>
    </div>
</template>

<script>
export default {
    props: {
        listRoute: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            loading: false,
            items: [
                t('quality-control::nequas.fbc_results'),
                t('quality-control::nequas.adlc_results'),
                t('quality-control::nequas.px_results'),
            ],
            initialFilters: {
                type: 'fbc',
                allowed_results: 1,
                filter_created_at: null,
                created_at: null,
            },
            createdAtCalendar: null,
            table: null,
            showFilters: false,
            columnsList: {
                fbcColumns: [
                    {
                        text: t('quality-control::nequas.grid.label.id'),
                        value: 'id',
                        sortable: false,
                        width: 170,
                    },
                    {
                        text: t('quality-control::nequas.grid.label.prn'),
                        value: 'prn',
                        width: 125,
                        sortable: true,
                    },
                    {
                        text: t('quality-control::nequas.grid.label.sample_ref'),
                        value: 'sample_ref',
                        width: 125,
                        sortable: true,
                    },
                    {
                        text: t('quality-control::nequas.grid.label.created_at'),
                        value: 'created_at',
                        width: 150,
                        sortable: true,
                    },
                    {
                        text: t('quality-control::grid.label.pharmacy_site'),
                        value: 'pharmacy_site',
                        width: 150,
                        sortable: true,
                    },
                    {
                        text: t('quality-control::nequas.grid.label.wbc'),
                        value: 'sample_properties.wbc',
                        fields: 'sample_properties',
                        width: 75,
                        sortable: false,
                    },
                    {
                        text: t('quality-control::nequas.grid.label.rbc'),
                        value: 'sample_properties.rbc',
                        fields: 'sample_properties',
                        width: 75,
                        sortable: false,
                    },
                    {
                        text: t('quality-control::nequas.grid.label.hgb'),
                        value: 'sample_properties.hgb',
                        fields: 'sample_properties',
                        width: 75,
                        sortable: false,
                    },
                    {
                        text: t('quality-control::nequas.grid.label.hct'),
                        value: 'sample_properties.hct',
                        fields: 'sample_properties',
                        width: 75,
                        sortable: false,
                    },
                    {
                        text: t('quality-control::nequas.grid.label.mcv'),
                        value: 'sample_properties.mcv',
                        fields: 'sample_properties',
                        width: 75,
                        sortable: false,
                    },
                    {
                        text: t('quality-control::nequas.grid.label.mch'),
                        value: 'sample_properties.mch',
                        fields: 'sample_properties',
                        width: 75,
                        sortable: false,
                    },
                    {
                        text: t('quality-control::nequas.grid.label.mchc'),
                        value: 'sample_properties.mchc',
                        fields: 'sample_properties',
                        width: 75,
                        sortable: false,
                    },
                    {
                        text: t('quality-control::nequas.grid.label.platelet'),
                        value: 'sample_properties.plt',
                        fields: 'sample_properties',
                        width: 100,
                        sortable: false,
                        align: 'center',
                    },
                ],
                adlcColumns: [
                    {
                        text: t('quality-control::nequas.grid.label.id'),
                        value: 'id',
                        sortable: false,
                        width: 170,
                    },
                    {
                        text: t('quality-control::nequas.grid.label.prn'),
                        value: 'prn',
                        width: 125,
                        sortable: true,
                    },
                    {
                        text: t('quality-control::nequas.grid.label.sample_ref'),
                        value: 'sample_ref',
                        width: 125,
                        sortable: true,
                    },
                    {
                        text: t('quality-control::nequas.grid.label.created_at'),
                        value: 'created_at',
                        width: 150,
                        sortable: true,
                    },
                    {
                        text: t('quality-control::grid.label.pharmacy_site'),
                        value: 'pharmacy_site',
                        width: 150,
                        sortable: true,
                    },
                    {
                        text: t('quality-control::nequas.grid.label.wbc'),
                        value: 'sample_properties.wbc',
                        fields: 'sample_properties',
                        width: 75,
                        sortable: false,
                    },
                    {
                        text: t('quality-control::nequas.grid.label.neut'),
                        value: 'sample_properties.neut',
                        fields: 'sample_properties',
                        width: 75,
                        sortable: false,
                    },
                    {
                        text: t('quality-control::nequas.grid.label.lym'),
                        value: 'sample_properties.lym',
                        fields: 'sample_properties',
                        width: 75,
                        sortable: false,
                    },
                    {
                        text: t('quality-control::nequas.grid.label.mono'),
                        value: 'sample_properties.mono',
                        fields: 'sample_properties',
                        width: 75,
                        sortable: false,
                    },
                    {
                        text: t('quality-control::nequas.grid.label.eos'),
                        value: 'sample_properties.eos',
                        fields: 'sample_properties',
                        width: 75,
                        sortable: false,
                    },
                    {
                        text: t('quality-control::nequas.grid.label.bas'),
                        value: 'sample_properties.bas',
                        fields: 'sample_properties',
                        width: 75,
                        sortable: false,
                        align: 'center',
                    },
                ],
                pxColumns: [
                    {
                        text: t('quality-control::nequas.grid.label.id'),
                        value: 'id',
                        sortable: false,
                        width: 170,
                    },
                    {
                        text: t('quality-control::nequas.grid.label.prn'),
                        value: 'prn',
                        width: 125,
                        sortable: true,
                    },
                    {
                        text: t('quality-control::nequas.grid.label.sample_ref'),
                        value: 'sample_ref',
                        width: 125,
                        sortable: true,
                    },
                    {
                        text: t('quality-control::nequas.grid.label.created_at'),
                        value: 'created_at',
                        width: 150,
                        sortable: true,
                    },
                    {
                        text: t('quality-control::grid.label.pharmacy_site'),
                        value: 'pharmacy_site',
                        width: 150,
                        sortable: true,
                    },
                    {
                        text: t('quality-control::nequas.grid.label.wbc'),
                        value: 'sample_properties.wbc',
                        fields: 'sample_properties',
                        width: 75,
                        sortable: false,
                    },
                    {
                        text: t('quality-control::nequas.grid.label.rbc'),
                        value: 'sample_properties.rbc',
                        fields: 'sample_properties',
                        width: 75,
                        sortable: false,
                    },
                    {
                        text: t('quality-control::nequas.grid.label.hgb'),
                        value: 'sample_properties.hgb',
                        fields: 'sample_properties',
                        width: 75,
                        sortable: false,
                    },
                    {
                        text: t('quality-control::nequas.grid.label.hct'),
                        value: 'sample_properties.hct',
                        fields: 'sample_properties',
                        width: 75,
                        sortable: false,
                    },
                    {
                        text: t('quality-control::nequas.grid.label.mcv'),
                        value: 'sample_properties.mcv',
                        fields: 'sample_properties',
                        width: 75,
                        sortable: false,
                    },
                    {
                        text: t('quality-control::nequas.grid.label.mch'),
                        value: 'sample_properties.mch',
                        fields: 'sample_properties',
                        width: 75,
                        sortable: false,
                    },
                    {
                        text: t('quality-control::nequas.grid.label.mchc'),
                        value: 'sample_properties.mchc',
                        fields: 'sample_properties',
                        width: 75,
                        sortable: false,
                    },
                    {
                        text: t('quality-control::nequas.grid.label.rdw'),
                        value: 'sample_properties.rdw',
                        fields: 'sample_properties',
                        width: 75,
                        sortable: false,
                    },
                    {
                        text: t('quality-control::nequas.grid.label.plt'),
                        value: 'sample_properties.plt',
                        fields: 'sample_properties',
                        width: 75,
                        sortable: false,
                    },
                    {
                        text: t('quality-control::nequas.grid.label.mpv'),
                        value: 'sample_properties.mpv',
                        fields: 'sample_properties',
                        width: 75,
                        sortable: false,
                    },
                    {
                        text: t('quality-control::nequas.grid.label.neut'),
                        value: 'sample_properties.neut',
                        fields: 'sample_properties',
                        width: 75,
                        sortable: false,
                    },
                    {
                        text: t('quality-control::nequas.grid.label.lym'),
                        value: 'sample_properties.lym',
                        fields: 'sample_properties',
                        width: 75,
                        sortable: false,
                    },
                    {
                        text: t('quality-control::nequas.grid.label.mono'),
                        value: 'sample_properties.mono',
                        fields: 'sample_properties',
                        width: 75,
                        sortable: false,
                    },
                    {
                        text: t('quality-control::nequas.grid.label.eos'),
                        value: 'sample_properties.eos',
                        fields: 'sample_properties',
                        width: 75,
                        sortable: false,
                    },
                    {
                        text: t('quality-control::nequas.grid.label.bas'),
                        value: 'sample_properties.bas',
                        fields: 'sample_properties',
                        width: 75,
                        sortable: false,
                        align: 'center',
                    },
                    {
                        text: t('quality-control::nequas.grid.label.neu%'),
                        value: 'sample_properties.neu%',
                        fields: 'sample_properties',
                        width: 75,
                        sortable: false,
                    },
                    {
                        text: t('quality-control::nequas.grid.label.lym%'),
                        value: 'sample_properties.lym%',
                        fields: 'sample_properties',
                        width: 75,
                        sortable: false,
                    },
                    {
                        text: t('quality-control::nequas.grid.label.mon%'),
                        value: 'sample_properties.mon%',
                        fields: 'sample_properties',
                        width: 75,
                        sortable: false,
                    },
                    {
                        text: t('quality-control::nequas.grid.label.eos%'),
                        value: 'sample_properties.eos%',
                        fields: 'sample_properties',
                        width: 75,
                        sortable: false,
                    },
                    {
                        text: t('quality-control::nequas.grid.label.bas%'),
                        value: 'sample_properties.bas%',
                        fields: 'sample_properties',
                        width: 75,
                        sortable: false,
                    },
                ],
            },
            hiddenColumnsList: {
                fbcColumns: ['id'],
                adlcColumns: ['id'],
                pxColumns: ['id'],
            },
            hiddenColumns: [],
            columns: [],
            gridFiltered: false,
        };
    },
    computed: {
        period() {
            return this.$store.state.qualityControl.neqas.period;
        },
        reports() {
            return this.$store.state.qualityControl.neqas.reports;
        },
        currentReport() {
            return this.$store.state.qualityControl.neqas.currentReport;
        },
        currentFilters() {
            return this.$store.state.qualityControl.neqas.filters;
        },
        tab: {
            get() {
                return this.$store.state.qualityControl.neqas.tab;
            },
            set(tab) {
                this.$store.dispatch('data_table/changeTab', tab);
                if (tab === 0) {
                    this.initialFilters.type = 'fbc';
                    this.columns = this.columnsList.fbcColumns;
                    this.hiddenColumns = this.hiddenColumnsList.fbcColumns;
                    if (this.$refs.table0) {
                        this.$refs.table0[0].datatableKey++;
                    }
                }
                if (tab === 1) {
                    this.columns = this.columnsList.adlcColumns;
                    this.hiddenColumns = this.hiddenColumnsList.adlcColumns;
                    this.initialFilters.type = 'adlc';

                    if (this.$refs.table1) {
                        this.$refs.table1[0].datatableKey++;
                    }
                }
                if (tab === 2) {
                    this.columns = this.columnsList.pxColumns;
                    this.hiddenColumns = this.hiddenColumnsList.pxColumns;
                    this.initialFilters.type = 'px';

                    if (this.$refs.table2) {
                        this.$refs.table2[0].datatableKey++;
                    }
                }
                return this.$store.dispatch('qualityControl/setNeqasTab', tab);
            },
        },
    },
    watch: {
        'initialFilters.filter_created_at': {
            handler: _.debounce(function (val) {
                this.gridFiltered = false;
                _.delay(() => {
                    return this.dateFilterConverter(val);
                }, 300);
            }),
        },
    },
    created() {
        this.$store.dispatch('data_table/changeTab', 0);
        this.columns = this.columnsList.fbcColumns;
        this.hiddenColumns = this.hiddenColumnsList.fbcColumns;
    },
    methods: {
        applyFiltersToTable(tableRef, filters) {
            tableRef.$refs.grid.initialFilters = filters;
            // tableRef.$refs.grid.applyFilters(true);
            tableRef.datatableKey++;
        },
        doSearch() {
            if (!this.initialFilters.filter_created_at) {
                return;
            }

            if (this.tab === 0) this.applyFiltersToTable(this.$refs.table0[0], this.initialFilters);
            if (this.tab === 1) this.applyFiltersToTable(this.$refs.table1[0], this.initialFilters);
            if (this.tab === 2) this.applyFiltersToTable(this.$refs.table2[0], this.initialFilters);

            this.setReportPeriod(this.initialFilters.filter_created_at);
            this.gridFiltered = true;
        },
        generateReport() {
            this.loading = true;
            return axios
                .post(route('api.v1.neqas-reports.store'), {
                    created_at: this.initialFilters.created_at,
                    sort: 'created_at',
                })
                .then((response) => {
                    const data = response.data.data;
                    const reportName = data.file_name;
                    this.$root.notify(
                        'info',
                        t('quality-control::nequas.grid.notification.generated_report'),
                        reportName
                    );
                    this.$store.dispatch('qualityControl/fetchNeqasReprots', new Object());
                })
                .catch((error) => {
                    this.$root.notify(
                        'error',
                        t('quality-control::nequas.grid.generate.report.fail'),
                        error.response.data.error
                    );
                })
                .finally(() => {
                    _.delay(() => {
                        this.loading = false;
                    }, 1000);
                });
        },
        reset() {
            this.initialFilters.filter_created_at = null;
            this.initialFilters.created_at = null;
            this.$store.dispatch('qualityControl/setNeqasPeriod', null);

            if (this.tab === 0) this.applyFiltersToTable(this.$refs.table0[0], this.initialFilters);
            if (this.tab === 1) this.applyFiltersToTable(this.$refs.table1[0], this.initialFilters);
            if (this.tab === 2) this.applyFiltersToTable(this.$refs.table2[0], this.initialFilters);

            // this.setReportPeriod(this.initialFilters.filter_created_at);
            // this.gridFiltered = true;
            //
            // return this.table.datatableKey++;
        },
        dateFilterConverter(val) {
            let dates = _.clone(val);
            if (!val) {
                return this.$store.dispatch('qualityControl/setNeqasFilters', new Object());
            }
            if (val && val.length <= 1) {
                dates[0] = val[0];
                const currentFilters = { created_at: dates.join(','), filter_created_at: val };
                this.initialFilters.created_at = currentFilters.created_at;
                return this.$store.dispatch('qualityControl/setNeqasFilters', currentFilters);
            }
            if (moment(val[0]).isAfter(moment(val[1]))) {
                dates[0] = `gte:${val[1]}`;
                dates[1] = `lte:${val[0]}`;
            }
            if (moment(val[1]).isAfter(moment(val[0]))) {
                dates[0] = `gte:${val[0]}`;
                dates[1] = `lte:${val[1]}`;
            }
            if (moment(val[0]).isSame(moment(val[1]))) {
                dates[0] = `gte:${val[0]}`;
                dates[1] = `lte:${val[0]}`;
            }
            const currentFilters = { created_at: dates.join(','), filter_created_at: val };
            this.initialFilters.created_at = currentFilters.created_at;
            return this.$store.dispatch('qualityControl/setNeqasFilters', currentFilters);
        },
        setReportPeriod(val) {
            let dates = _.clone(val);

            if (!val) {
                return this.$store.dispatch('qualityControl/setNeqasPeriod', null);
            }
            if (val && val.length <= 1) {
                const period = `${moment(val[0]).format(this.$root.dateFormat)} - ${moment(val[0]).format(
                    this.$root.dateFormat
                )}`;
                return this.$store.dispatch('qualityControl/setNeqasPeriod', period);
            }
            if (moment(val[0]).isAfter(moment(val[1]))) {
                dates[0] = val[1];
                dates[1] = val[0];
            }
            if (moment(val[1]).isAfter(moment(val[0]))) {
                dates[0] = val[0];
                dates[1] = val[1];
            }
            if (moment(val[0]).isSame(moment(val[1]))) {
                dates[0] = val[0];
                dates[1] = val[0];
            }
            const period = `${moment(dates[0]).format(this.$root.dateFormat)} - ${moment(dates[1]).format(
                this.$root.dateFormat
            )}`;
            return this.$store.dispatch('qualityControl/setNeqasPeriod', period);
        },
    },
};
</script>
